import { useState } from 'react'
import type { CSSProperties } from 'react'
import { Icon } from './Icon'

export type LeaveTypePillSize = 'sm' | 'md'

interface LeaveTypePillProps {
  leaveTypeId: string
  label: string
  /** Hex / oklch / token reference for this leave type's accent color. */
  color: string
  selected?: boolean
  docRequired?: boolean
  onClick?: () => void
  size?: LeaveTypePillSize
}

/**
 * Two-mode visual primitive for leave types.
 *
 * - **Button mode** (`onClick` provided): a tappable pill used in selectors
 *   such as the apply-leave 3-column grid. `selected` adds a 2px border in
 *   the type's color and tints the background.
 * - **Dot mode** (no `onClick`, `size === 'sm'`): an inline label with a
 *   small color dot, used in history rows and calendar cells.
 *
 * `docRequired` shows a paperclip indicator on the right.
 */
export function LeaveTypePill({
  leaveTypeId,
  label,
  color,
  selected = false,
  docRequired = false,
  onClick,
  size = 'md',
}: LeaveTypePillProps): JSX.Element {
  const isButton = typeof onClick === 'function'
  const [hover, setHover] = useState<boolean>(false)
  const [pressed, setPressed] = useState<boolean>(false)

  if (!isButton && size === 'sm') {
    return (
      <span
        data-testid="leave-type-pill"
        data-leave-type={leaveTypeId}
        data-mode="dot"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--text-2)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: color,
          }}
        />
        <span>{label}</span>
        {docRequired ? (
          <span aria-label="documentation required" title="Documentation required" style={{ display: "inline-flex", alignItems: "center" }}>
            <Icon name="attach_file" size={12} aria-hidden="true" />
          </span>
        ) : null}
      </span>
    )
  }

  const padding = size === 'sm' ? '4px 8px' : '8px 12px'
  const fontSize = size === 'sm' ? 11 : 12

  const baseBg = selected
    ? `color-mix(in oklch, ${color} 14%, var(--surface))`
    : 'var(--surface)'
  const hoverBg = isButton && hover && !selected
    ? `color-mix(in oklch, ${color} 8%, var(--surface))`
    : null

  const buttonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding,
    fontSize,
    fontWeight: 500,
    borderRadius: 999,
    background: hoverBg ?? baseBg,
    color: 'var(--text)',
    border: selected ? `2px solid ${color}` : '1px solid var(--border)',
    cursor: isButton ? 'pointer' : 'default',
    transition:
      'background var(--motion-duration-base) var(--motion-ease-out),' +
      ' border-color var(--motion-duration-base) var(--motion-ease-out),' +
      ' color var(--motion-duration-base) var(--motion-ease-out),' +
      ' transform var(--motion-duration-base) var(--motion-ease-out)',
    minHeight: 44,
  }

  if (isButton && pressed) {
    buttonStyle.transform = 'scale(0.98)'
    buttonStyle.transitionDuration = 'var(--motion-duration-fast)'
  }

  const Tag = isButton ? 'button' : 'span'

  return (
    <Tag
      type={isButton ? 'button' : undefined}
      data-testid="leave-type-pill"
      data-leave-type={leaveTypeId}
      data-mode={isButton ? 'button' : 'dot'}
      data-selected={selected ? 'true' : 'false'}
      data-active={selected ? 'true' : 'false'}
      aria-pressed={isButton ? selected : undefined}
      onClick={onClick}
      onMouseEnter={isButton ? () => setHover(true) : undefined}
      onMouseLeave={isButton ? () => {
        setHover(false)
        setPressed(false)
      } : undefined}
      onMouseDown={isButton ? () => setPressed(true) : undefined}
      onMouseUp={isButton ? () => setPressed(false) : undefined}
      style={buttonStyle}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
        }}
      />
      <span>{label}</span>
      {docRequired ? (
        <span aria-label="documentation required" title="Documentation required" style={{ display: "inline-flex", alignItems: "center" }}>
          <Icon name="attach_file" size={12} aria-hidden="true" />
        </span>
      ) : null}
    </Tag>
  )
}
